// AddTimetableEntryModal.tsx
import React, { useState, useEffect, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import useClassesStore from '../../../stateManagers/store/useClassesStore';
import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';
import { GlobalChipSelector, ChipOption } from '../../../components/GlobalComponents/GlobalChipSelector';
import useTimetableTypes from '../../../stateManagers/hooks/useTimetableTypes';
import { useTimeSlots } from '../../../stateManagers/hooks/useTimeSlots';
import { useSubjectsByClass } from '../../../stateManagers/hooks/useSubjectsByClass';
import { modalStyles } from './subparts/modalStyles';
import TimetableEntryCard from './subparts/TimetableEntryCard';
import { AddTimetableEntryModalProps, TimetableEntryFormData } from './subparts/types';
import apiClient from '../../../stateManagers/services/axiosInterceptor';
import { hasNoSubject, hasNoTeacher } from './subparts/timetableSlotTypes';

const AddTimetableEntryModal: React.FC<AddTimetableEntryModalProps> = ({
  open,
  onClose,
  onSuccess,
  context,
}) => {
  const toast = useRef<Toast>(null);

  const [formData, setFormData] = useState<TimetableEntryFormData>({
    timetableTypeId: '',
    classId: '',
    section: null,
    entries: [{
      id: crypto.randomUUID(),
      timeSlotId: '',
      subjectId: '',
      teacherAssignmentId: '',
      daysOfWeek: [],
      roomNumber: '',
    }],
  });

  const [loading, setLoading] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  const { classOptions, fetchClasses, isFetching } = useClassesStore();
  const timetableTypes = useTimetableTypes(true);

  const selectedClass = classOptions.find((c) => c.id === formData.classId);
  const selectedClassSections = selectedClass?.sections ?? [];
  const hasSections = selectedClassSections.length > 0;

  const classIdForSubjects = formData.classId || null;
  const { subjectOptions, loading: subjectsLoading, error: subjectsError } = useSubjectsByClass(classIdForSubjects, true);

  const hasTimetableType = !!formData.timetableTypeId || !!context?.timetableTypeId;
  const hasClass = !!formData.classId;
  const hasSectionIfNeeded = !hasSections || !!formData.section;
  const canShowEntries = hasTimetableType && hasClass && hasSectionIfNeeded;

  const timetableTypeIdForHook = canShowEntries
    ? formData.timetableTypeId || context?.timetableTypeId || ''
    : null;

  const { timeSlots, loading: timeSlotsLoading, error: timeSlotsError } = useTimeSlots(timetableTypeIdForHook);

  const hasExistingEntries = context?.existingItems && context.existingItems.length > 0;

  useEffect(() => {
    if (open) fetchClasses(true);
  }, [open, fetchClasses]);

  useEffect(() => {
    if (open) {
      if (context) {
        setFormData({
          timetableTypeId: context.timetableTypeId,
          classId: context.classId,
          section: context.section,
          entries: [{
            id: crypto.randomUUID(),
            timeSlotId: '',
            subjectId: '',
            teacherAssignmentId: '',
            daysOfWeek: [],
            roomNumber: '',
          }],
        });
      } else {
        setFormData({
          timetableTypeId: '',
          classId: '',
          section: null,
          entries: [{
            id: crypto.randomUUID(),
            timeSlotId: '',
            subjectId: '',
            teacherAssignmentId: '',
            daysOfWeek: [],
            roomNumber: '',
          }],
        });
      }
    }
  }, [open, context]);

  useEffect(() => {
    if (context?.periodLabel && timeSlots.length > 0 && formData.entries.length === 1) {
      const match = timeSlots.find((slot) =>
        slot.label.toLowerCase().includes(context.periodLabel.toLowerCase())
      );
      if (match) {
        setFormData((prev) => ({
          ...prev,
          entries: [{ ...prev.entries[0], timeSlotId: match.id }],
        }));
      }
    }
  }, [timeSlots, context?.periodLabel]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 400);
  };

  const handleAddEntry = () => {
    setFormData((prev) => ({
      ...prev,
      entries: [...prev.entries, {
        id: crypto.randomUUID(),
        timeSlotId: '',
        subjectId: '',
        teacherAssignmentId: '',
        daysOfWeek: [],
        roomNumber: '',
      }],
    }));
  };

  const handleRemoveEntry = (id: string) => {
    if (formData.entries.length === 1) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Warning',
        detail: 'At least one entry is required',
        life: 3000,
      });
      return;
    }
    setFormData((prev) => ({
      ...prev,
      entries: prev.entries.filter((entry) => entry.id !== id),
    }));
  };

  const handleUpdateEntry = (
    id: string,
    field: 'timeSlotId' | 'subjectId' | 'teacherAssignmentId' | 'roomNumber',
    value: string
  ) => {
    // Duplicate subject guard
    if (field === 'subjectId' && value) {
      const isDuplicate = formData.entries.some(
        (entry) => entry.id !== id && entry.subjectId === value
      );
      if (isDuplicate) {
        const selectedSubject = subjectOptions.find((sub) => sub.value === value);
        toast.current?.show({
          severity: 'error',
          summary: 'Duplicate Subject',
          detail: `Subject "${selectedSubject?.label || value}" is already selected in another entry`,
          life: 4000,
        });
        return;
      }
    }

    setFormData((prev) => ({
      ...prev,
      entries: prev.entries.map((entry) => {
        if (entry.id !== id) return entry;
        const updated = { ...entry, [field]: value };

        // Clear subject for all non-teaching slots; only clear teacher for break (optional for others)
        if (field === 'timeSlotId') {
          const newSlotType = timeSlots.find((s) => s.id === value)?.slotType;
          if (hasNoSubject(newSlotType)) {
            updated.subjectId = '';
          }
          if (hasNoTeacher(newSlotType)) {
            updated.teacherAssignmentId = '';
          }
        }

        return updated;
      }),
    }));
  };

  const handleUpdateDays = (id: string, days: string[]) => {
    setFormData((prev) => ({
      ...prev,
      entries: prev.entries.map((entry) =>
        entry.id === id ? { ...entry, daysOfWeek: days } : entry
      ),
    }));
  };

  const getOccupiedDaysForSlot = (currentEntryId: string, timeSlotId: string): string[] => {
    return formData.entries
      .filter((entry) => entry.id !== currentEntryId && entry.timeSlotId === timeSlotId)
      .flatMap((entry) => entry.daysOfWeek);
  };

  /* ── Chip options (Timetable Type / Class / Section) ─────────────── */

  const timetableTypeChipOptions = timetableTypes
  .filter((t) => t.id && !t.label.toLowerCase().startsWith('select'))
  .map((t) => ({
    value: t.id,
    label: t.label,
  }));

  const classChipOptions = classOptions.map((c) => ({
    value: c.id,
    label: c.label,
  }));

  const sectionChipOptions = selectedClassSections.map((s) => ({
    value: s,
    label: `Section ${s}`,
  }));

  /* ── Chip options (Subject / Time Slot — used by TimetableEntryCard) ── */

  const timeSlotChipOptions: ChipOption[] = timeSlots.map((slot) => ({
    value: slot.id,
    label: slot.label,
  }));

  const subjectChipOptions: ChipOption[] = subjectOptions.map((sub) => ({
    value: sub.value,
    label: sub.label,
  }));

  /* ══════════════════════════════════════════════════════════════════
     SUBMIT
     - teacherAssignmentId holds the real teacher_id (set by card)
     - One POST per entry fired in parallel via Promise.all
     - Slot type rules:
       - 'break'                              -> no subject, no teacher
       - 'power_nap' / 'workbook' / 'activities' -> no subject, teacher OPTIONAL
       - everything else (class, lab, assembly)   -> subject + teacher REQUIRED
  ══════════════════════════════════════════════════════════════════ */
  const handleSubmit = async () => {
    // Top-level validation
    if (!formData.timetableTypeId || !formData.classId) {
      toast.current?.show({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Please select timetable type and class',
        life: 5000,
      });
      return;
    }

    if (hasSections && !formData.section) {
      toast.current?.show({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Please select a section',
        life: 5000,
      });
      return;
    }

    // Per-entry validation + day-overlap check
    const slotDayMap = new Map<string, string[]>();

    for (const entry of formData.entries) {
      const entrySlotType = timeSlots.find((s) => s.id === entry.timeSlotId)?.slotType;
      const noSubjectEntry = hasNoSubject(entrySlotType);
      const teacherRequired = !noSubjectEntry; // only required for teaching slots

      if (
        !entry.timeSlotId ||
        (!noSubjectEntry && !entry.subjectId) ||
        (teacherRequired && !entry.teacherAssignmentId) ||
        entry.daysOfWeek.length === 0
      ) {
        toast.current?.show({
          severity: 'error',
          summary: 'Validation Error',
          detail: 'Please complete all fields and select at least one day for each entry',
          life: 5000,
        });
        return;
      }

      const existingDays = slotDayMap.get(entry.timeSlotId) || [];
      const overlap = entry.daysOfWeek.some((day) => existingDays.includes(day));
      if (overlap) {
        const slotLabel =
          timeSlots.find((s) => s.id === entry.timeSlotId)?.label || entry.timeSlotId;
        toast.current?.show({
          severity: 'error',
          summary: 'Day Overlap',
          detail: `Time slot "${slotLabel}" has overlapping days. Each day can only be used once per period.`,
          life: 6000,
        });
        return;
      }
      slotDayMap.set(entry.timeSlotId, [...existingDays, ...entry.daysOfWeek]);
    }

    // Build one payload per entry
    const payloads = formData.entries.map((entry) => {
      const entrySlotType = timeSlots.find((s) => s.id === entry.timeSlotId)?.slotType;
      const noSubjectEntry = hasNoSubject(entrySlotType);
      return {
        class_id: parseInt(formData.classId),
        section: hasSections ? formData.section : '',
        subject_id: noSubjectEntry ? null : parseInt(entry.subjectId),
        teacher_id: entry.teacherAssignmentId ? parseInt(entry.teacherAssignmentId) : null,
        timetable_type_id: parseInt(formData.timetableTypeId),
        time_slot_id: parseInt(entry.timeSlotId),
        day_of_week: entry.daysOfWeek,
        room_number: entry.roomNumber || null,
      };
    });

    setLoading(true);
    try {
      const responses = await Promise.all(
        payloads.map((payload) => apiClient.post('add-timetable', payload))
      );

      const failed = responses.filter((res) => res.data.status !== 'success');
      const successCount = responses.length - failed.length;

      if (failed.length === 0) {
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: `${successCount} timetable ${successCount === 1 ? 'entry' : 'entries'} added successfully!`,
          life: 4000,
        });
        setTimeout(() => {
          onSuccess();
          handleClose();
        }, 300);
      } else if (successCount > 0) {
        toast.current?.show({
          severity: 'warn',
          summary: 'Partial Success',
          detail: `${successCount} succeeded, ${failed.length} failed. — ${failed[0]?.data?.message || 'Unknown error'}`,
          life: 5000,
        });
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Failed',
          detail: failed[0]?.data?.message || 'All entries failed to save.',
          life: 4000,
        });
      }
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.message || 'Failed to add entries',
        life: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  /* ── Header ───────────────────────────────────────────────────── */

  const currentTimetableTypeLabel =
    timetableTypes.find((t) => t.id === formData.timetableTypeId)?.label ||
    (context?.timetableTypeId
      ? timetableTypes.find((t) => t.id === context.timetableTypeId)?.label
      : null);

  const currentClassLabel =
    classOptions.find((c) => c.id === formData.classId)?.label ||
    (context?.classId
      ? classOptions.find((c) => c.id === context.classId)?.label
      : null);

  const currentSectionLabel =
    formData.section || context?.section
      ? `Section ${formData.section || context?.section}`
      : null;

  const headerTemplate = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '8px 0' }}>
      <div style={{
        width: '54px', height: '54px', borderRadius: '14px',
        background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: 'white', fontSize: '24px', fontWeight: '700',
        boxShadow: '0 8px 24px rgba(59, 130, 246, 0.3)',
        flexShrink: 0, border: '3px solid white',
      }}>
        <i className={hasExistingEntries ? 'pi pi-calendar-times' : 'pi pi-plus-circle'} />
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: '20px', fontWeight: '700', color: '#1e40af', marginBottom: '4px' }}>
          {hasExistingEntries
            ? `Manage Entries (${context?.existingItems.length})`
            : currentTimetableTypeLabel || 'Add Timetable Entry'}
        </div>
        <div style={{ fontSize: '13px', color: '#64748b', fontWeight: '500' }}>
          {context ? (
            <>
              {context.day} • {context.periodLabel}
              {currentClassLabel && (
                <>
                  {' • '}<strong>{currentClassLabel}</strong>
                  {currentSectionLabel && ` ${currentSectionLabel}`}
                </>
              )}
            </>
          ) : currentTimetableTypeLabel ? (
            currentClassLabel ? (
              <>
                <strong>{currentClassLabel}</strong>
                {currentSectionLabel && ` ${currentSectionLabel}`}
              </>
            ) : 'Select class and section below'
          ) : 'Select timetable type, class, and section'}
        </div>
      </div>
    </div>
  );

  useEffect(() => {
    if (isClosing) {
      const dialog = document.querySelector('.timetable-entry-modal .p-dialog');
      const mask = document.querySelector('.timetable-entry-modal');
      if (dialog) dialog.classList.add('dialog-closing');
      if (mask) mask.classList.add('mask-closing');
    }
  }, [isClosing]);

  return (
    <>
      <Toast ref={toast} position="top-right" />

      <Dialog
        visible={open && !isClosing}
        onHide={handleClose}
        header={headerTemplate}
        style={{ width: '90vw', maxWidth: '1050px', borderRadius: '24px', overflow: 'hidden' }}
        contentStyle={{
          padding: '18px',
          background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
          maxHeight: 'calc(90vh - 120px)',
          overflowY: 'auto',
        }}
        modal
        dismissableMask
        draggable={false}
        resizable={false}
        blockScroll
        maskClassName="timetable-entry-modal"
      >
        <style>{modalStyles}</style>

        {hasExistingEntries && context && (
          <div style={{ marginBottom: '24px' }}>
            <p style={{ textAlign: 'center', color: '#475569', fontWeight: 500, marginBottom: '16px' }}>
              Existing entries for this time slot
            </p>
            {context.existingItems.map((item, index) => (
              <div key={index} style={{
                background: 'rgba(255,255,255,0.9)', padding: '16px',
                borderRadius: '12px', border: '1px solid rgba(59,130,246,0.15)',
                marginBottom: '12px',
              }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '14px' }}>
                  <div><strong style={{ color: '#1e40af' }}>Subject:</strong> {item.subject_name}</div>
                  <div><strong style={{ color: '#1e40af' }}>Teacher:</strong> {item.teacher_name || '—'}</div>
                  <div><strong style={{ color: '#1e40af' }}>Type:</strong> {item.slot_type === 'break' ? 'Break' : 'Class'}</div>
                  <div><strong style={{ color: '#1e40af' }}>Days:</strong> {JSON.parse(item.day_of_week || '[]').join(', ')}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div className="field-section">
            <div className="section-header">
              <div className="icon-circle"><i className="pi pi-sitemap" /></div>
              <span>Select Timetable & Class</span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <GlobalChipSelector
                label="Timetable Type"
                icon="pi pi-calendar"
                value={formData.timetableTypeId}
                onChange={(v) => setFormData({
                  ...formData,
                  timetableTypeId: v,
                  entries: [{
                    id: crypto.randomUUID(),
                    timeSlotId: '', subjectId: '',
                    teacherAssignmentId: '', daysOfWeek: [], roomNumber: '',
                  }],
                })}
                options={timetableTypeChipOptions}
                required
                disabled={loading || !!context?.timetableTypeId}
                emptyMessage="No timetable types available"
              />

              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
              }}>
                <GlobalChipSelector
                  label="Class"
                  icon="pi pi-users"
                  value={formData.classId}
                  onChange={(v) => setFormData({
                    ...formData,
                    classId: v,
                    section: null,
                    entries: [{
                      id: crypto.randomUUID(),
                      timeSlotId: '', subjectId: '',
                      teacherAssignmentId: '', daysOfWeek: [], roomNumber: '',
                    }],
                  })}
                  options={classChipOptions}
                  required
                  loading={isFetching}
                  disabled={loading || !!context?.classId}
                  emptyMessage="No classes available"
                />

                {hasSections && (
                  <GlobalChipSelector
                    label="Section"
                    icon="pi pi-list"
                    value={formData.section || ''}
                    onChange={(v) => setFormData({ ...formData, section: v })}
                    options={sectionChipOptions}
                    required
                    disabled={loading || !!context?.section}
                  />
                )}
              </div>

              {formData.classId && !hasSections && (
                <div className="info-note">
                  <i className="pi pi-info-circle" />
                  This class has no sections — entry will apply to the whole class
                </div>
              )}
            </div>
          </div>

          {canShowEntries && (
            <div className="field-section">
              <div className="section-header">
                <div className="icon-circle"><i className="pi pi-list" /></div>
                <span>Period Entries ({formData.entries.length})</span>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {formData.entries.map((entry, index) => {
                  const occupiedDays = getOccupiedDaysForSlot(entry.id, entry.timeSlotId);
                  const entrySlotType = timeSlots.find((s) => s.id === entry.timeSlotId)?.slotType ?? null;
                  return (
                    <TimetableEntryCard
                      key={entry.id}
                      entry={entry}
                      index={index}
                      classId={formData.classId}
                      section={formData.section}
                      canRemove={formData.entries.length > 1}
                      timeSlotOptions={timeSlotChipOptions}
                      subjectOptions={subjectChipOptions}
                      timeSlotsLoading={timeSlotsLoading}
                      subjectsLoading={subjectsLoading}
                      timeSlotsError={timeSlotsError}
                      subjectsError={subjectsError}
                      canShowEntries={canShowEntries}
                      loading={loading}
                      occupiedDays={occupiedDays}
                      slotType={entrySlotType}
                      onUpdate={handleUpdateEntry}
                      onUpdateDays={handleUpdateDays}
                      onRemove={handleRemoveEntry}
                    />
                  );
                })}

                <button
                  className="add-entry-button"
                  onClick={handleAddEntry}
                  disabled={loading || subjectsLoading || timeSlotsLoading}
                >
                  <i className="pi pi-plus-circle" />
                  Add Another Entry
                </button>
              </div>
            </div>
          )}

          <div style={{ textAlign: 'center', marginTop: '20px' }}>
            <GlobalButton
              label={
                loading
                  ? 'Adding Entries...'
                  : `Add ${formData.entries.length} ${formData.entries.length === 1 ? 'Entry' : 'Entries'}`
              }
              onClick={handleSubmit}
              variant="primary"
              size="medium"
              disabled={loading || isFetching || timeSlotsLoading || subjectsLoading}
              loading={loading}
            />
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default AddTimetableEntryModal;